import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe } from './api/hooks';
import type { Role } from '@alexiarest/shared';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { portadaDe, sinPnl, soloCosteo } from './auth/roles';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { LoadingState } from './components/EmptyState';
import { ToastProvider } from './components/Toast';
import { PeriodProvider } from './context/PeriodContext';
import { LocationProvider } from './context/LocationContext';
import { ThemeProvider } from './context/ThemeContext';
import { LoginPage } from './pages/LoginPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { setupPending } from './pages/setupShell';

/**
 * El resto de pantallas se cargan al navegar a ellas. Sin esto, abrir el
 * panel desde un movil descargaba las 14 pantallas y Recharts entero
 * (~900 KB) antes de pintar un solo numero.
 */
/**
 * `lazy` con red de seguridad para deploys: los chunks llevan hash y un
 * deploy los renombra. Si un navegador con la app abierta de antes pide un
 * chunk que ya no existe, en vez de una pagina en blanco se recarga UNA vez
 * (flag en sessionStorage para no entrar en bucle) y coge el index nuevo.
 */
function lazyPage<T extends object>(
  load: () => Promise<T>,
  name: keyof T & string,
): LazyExoticComponent<ComponentType> {
  /*
   * Un chunk que no se puede cargar es casi siempre un index.html de antes
   * de un despliegue. Se recarga la pagina para coger el index nuevo, con
   * una marca de tiempo para no entrar en bucle: si la recarga cae dentro
   * de la ventana del propio despliegue (invalidacion de CloudFront en
   * curso), a los 30 s se permite otra. Antes la marca no caducaba y el
   * segundo fallo ensenyaba el error tal cual (Alex, 6 sep 2026).
   */
  const CLAVE = 'alexia:chunk-reload';
  return lazy(() =>
    load()
      .then((m) => {
        sessionStorage.removeItem(CLAVE);
        return { default: m[name] as unknown as ComponentType };
      })
      .catch((err: unknown) => {
        const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
        if (Date.now() - ultima > 30_000) {
          sessionStorage.setItem(CLAVE, String(Date.now()));
          window.setTimeout(() => window.location.reload(), 1500);
          // Mientras tanto no se ensenya el error: la recarga viene en camino.
          return new Promise<{ default: ComponentType }>(() => undefined);
        }
        throw err;
      }),
  );
}

const AdminPage = lazyPage(() => import('./pages/AdminPage'), 'AdminPage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const AnalysisPage = lazyPage(() => import('./pages/AnalysisPage'), 'AnalysisPage');
const AssistantPage = lazyPage(() => import('./pages/AssistantPage'), 'AssistantPage');
const ExpensesPage = lazyPage(() => import('./pages/ExpensesPage'), 'ExpensesPage');
const IngredientsPage = lazyPage(() => import('./pages/IngredientsPage'), 'IngredientsPage');
const InventoryPage = lazyPage(() => import('./pages/InventoryPage'), 'InventoryPage');
const InvestmentsPage = lazyPage(() => import('./pages/InvestmentsPage'), 'InvestmentsPage');
const PricingPage = lazyPage(() => import('./pages/PricingPage'), 'PricingPage');
const RecipeDetailPage = lazyPage(() => import('./pages/RecipeDetailPage'), 'RecipeDetailPage');
const RecipesPage = lazyPage(() => import('./pages/RecipesPage'), 'RecipesPage');
const ResultsPage = lazyPage(() => import('./pages/ResultsPage'), 'ResultsPage');
const SalesPage = lazyPage(() => import('./pages/SalesPage'), 'SalesPage');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'SettingsPage');
const PosCallbackPage = lazyPage(() => import('./pages/PosCallbackPage'), 'PosCallbackPage');
const SuppliersPage = lazyPage(() => import('./pages/SuppliersPage'), 'SuppliersPage');
const FounderPage = lazyPage(() => import('./pages/FounderPage'), 'FounderPage');
const SetupWizardPage = lazyPage(() => import('./pages/SetupWizardPage'), 'SetupWizardPage');
const TargetsPage = lazyPage(() => import('./pages/TargetsPage'), 'TargetsPage');
const TaxesPage = lazyPage(() => import('./pages/TaxesPage'), 'TaxesPage');
const TreasuryPage = lazyPage(() => import('./pages/TreasuryPage'), 'TreasuryPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Guarda de la zona de administracion. La entrada del menu ya se oculta a
 * quien no es admin, pero la URL se puede teclear: sin esto, un cliente
 * curioso (o la demo publica) veria el armazon del panel antes del 403.
 */
function SoloAdmin(): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Comprobando permisos…" />;
  if (!me.data?.user?.platformAdmin) return <Navigate to="/" replace />;
  return <AdminPage />;
}

/**
 * Pantallas cerradas a un rol: la API ya le responde 403, y esta guarda
 * evita ensenyarle el armazon si teclea la URL. Se le lleva a su portada.
 */
function Vetada({
  para,
  children,
}: {
  para: (role?: Role) => boolean;
  children: JSX.Element;
}): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Comprobando permisos…" />;
  const role = me.data?.user?.role;
  if (para(role)) return <Navigate to={portadaDe(role)} replace />;
  return children;
}

/** El encargado y cocina no ven la P&L (decision del 21 ago). */
function SinPnl({ children }: { children: JSX.Element }): JSX.Element {
  return <Vetada para={sinPnl}>{children}</Vetada>;
}

/** Cocina tampoco ve ventas, gastos, tesoreria ni inversiones (7 sep). */
function SinFinanciero({ children }: { children: JSX.Element }): JSX.Element {
  return <Vetada para={soloCosteo}>{children}</Vetada>;
}

/**
 * Cerrojo Founder: quien se da de alta con invitación no tiene prueba
 * gratuita. Hasta que Stripe confirma el pago solo existe la página de pago —
 * sin menú, sin rutas, sin escapatoria—. El flag `founderPending` lo pone el
 * onboarding y lo quita el webhook al activarse la suscripción.
 */
function CerrojoFounder({ children }: { children: JSX.Element }): JSX.Element {
  const me = useMe();
  const { logout } = useAuth();
  if (me.isPending) return <LoadingState label="Cargando tu cuenta…" />;
  if (!me.data?.tenant?.founderPending) return children;
  return (
    <div className="founder-lock">
      <div className="founder-lock__bar">
        <span>Tu plaza Founder está reservada. Completa el pago para entrar en Alexia.</span>
        <button type="button" className="btn btn--ghost" onClick={logout}>
          Cerrar sesión
        </button>
      </div>
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Cargando…" />}>
          <FounderPage />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

/**
 * Cuestionario de configuracion inicial (Alex, 5 sep 2026): despues del
 * alta y del pago Founder, y antes de la app, el propietario responde los
 * pasos que faltan. Se guarda en el tenant, asi que sobrevive a cerrar el
 * navegador. La vuelta del OAuth del TPV (paso 4) tiene que pasar: esa ruta
 * se deja entrar aunque el cuestionario siga abierto.
 */
function CuestionarioInicial({ children }: { children: JSX.Element }): JSX.Element {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <LoadingState label="Cargando tu cuenta…" />;
  const enCallbackTpv = location.pathname.startsWith('/integraciones/');
  if (!setupPending(me.data?.tenant, me.data?.user?.role) || enCallbackTpv) return children;
  // Con su propio boundary: aqui no hay menu al que escapar, y un fallo de
  // render sin red dejaba la pagina en blanco (7 sep 2026).
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Cargando…" />}>
        <SetupWizardPage />
      </Suspense>
    </ErrorBoundary>
  );
}

/** La portada lleva la P&L dentro: al encargado se le lleva a Ingresos y a cocina a Recetas. */
function PortadaPorRol(): JSX.Element {
  const me = useMe();
  if (me.isPending) return <LoadingState label="Cargando…" />;
  const role = me.data?.user?.role;
  if (sinPnl(role)) return <Navigate to={portadaDe(role)} replace />;
  return <DashboardPage />;
}

function AppRoutes(): JSX.Element {
  const { loading, isAuthenticated, needsOnboarding } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingState label="Comprobando tu sesión…" />;
  if (!isAuthenticated) return <LoginPage />;
  // Sesion valida pero sin restaurante: primero el alta, luego la app.
  if (needsOnboarding) return <OnboardingPage />;

  return (
    // El boundary va DENTRO del router (necesita la ruta para resetearse) y
    // por fuera de las pantallas: un fallo de una no tumba la aplicacion.
    //
    // LocationProvider envuelve TAMBIEN el cerrojo Founder y el cuestionario:
    // los hooks de datos (useSaveTarget y compania) leen el local activo, y
    // el paso 5 del cuestionario los usa. Con el provider por dentro, el
    // primer cliente que llego a ese paso tras el multi-local (Mulita, 7 sep
    // 2026) se quedo con la pantalla en blanco.
    <LocationProvider>
    <CerrojoFounder>
    <CuestionarioInicial>
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingState label="Cargando…" />}>
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<PortadaPorRol />} />
        <Route path="asistente" element={<SinPnl><AssistantPage /></SinPnl>} />
        <Route path="ingredientes" element={<IngredientsPage />} />
        <Route path="inventario" element={<InventoryPage />} />
        <Route path="proveedores" element={<SuppliersPage />} />
        <Route path="recetas" element={<RecipesPage />} />
        <Route path="recetas/:id" element={<RecipeDetailPage />} />
        <Route path="precios" element={<PricingPage />} />
        <Route path="ingresos" element={<SinFinanciero><SalesPage /></SinFinanciero>} />
        <Route path="gastos" element={<SinFinanciero><ExpensesPage /></SinFinanciero>} />
        <Route path="analisis" element={<SinPnl><AnalysisPage /></SinPnl>} />
        <Route path="resultados" element={<SinPnl><ResultsPage /></SinPnl>} />
        <Route path="tesoreria" element={<SinFinanciero><TreasuryPage /></SinFinanciero>} />
        <Route path="inversiones" element={<SinFinanciero><InvestmentsPage /></SinFinanciero>} />
        <Route path="objetivos" element={<SinPnl><TargetsPage /></SinPnl>} />
        <Route path="impuestos" element={<SinPnl><TaxesPage /></SinPnl>} />
        <Route path="ajustes" element={<SettingsPage />} />
        {/* Vuelta del OAuth de un TPV (Square, Loyverse). Ver PosCallbackPage. */}
        <Route path="integraciones/:provider/callback" element={<PosCallbackPage />} />
        {/* Oferta Founder: fuera del menú, se llega por enlace directo. */}
        <Route path="founder" element={<FounderPage />} />
        <Route path="admin" element={<SoloAdmin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
      </Suspense>
    </ErrorBoundary>
    </CuestionarioInicial>
    </CerrojoFounder>
    </LocationProvider>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
        <AuthProvider>
          <PeriodProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </PeriodProvider>
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
